import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PropertyUnit } from 'src/app/models/interfaces/property-unit';
import { ApiService } from 'src/app/core/api/api.service';
import { PROPERTY_UNITS_ENDPOINTS } from 'src/app/core/api/endpoints/property-units.endpoints';
import { BUTTON_LOADING_ID } from 'src/app/shared/global/http-context-tokens';
import { HttpContext } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class PropertyUnitsService {
  private api = inject(ApiService);

  getPropertyUnits(params?: any): Observable<any> {
    return this.api
      .get<any>(PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNITS, {
        params,
      })
      .pipe(
        map((response) => {
          const transformedData = (response.data || []).map((unit: any) =>
            this.mapPropertyUnit(unit),
          );

          return {
            results: transformedData,
            count: response.meta?.pagination?.count || 0,
            next: response.meta?.pagination?.links?.next || null,
            previous: response.meta?.pagination?.links?.previous || null,
          };
        }),
      );
  }

  private mapPropertyUnit(unit: any): any {
    // Destructure to exclude building_type from spread
    const { building_type, ...restUnit } = unit;

    return {
      ...restUnit,
      id: unit.id,
      reference_number: unit.id?.toString() || '',
      region: unit.address_area || '',
      city: unit.address_city || '',
      district: unit.district || '',
      street: unit.address_street || '',
      unit_name: unit.custom_name || '',
      customer_name: this.getCustomerName(unit),
      property_type: building_type?.name || '',
      orders_count: unit.orders_count || 0,
      created_at: this.formatDate(unit.created_at) || '',
      last_visit_at: unit.last_visit_at
        ? this.formatDate(unit.last_visit_at)
        : null,
    };
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, '0');

    return `${year}/${month}/${day} ${formattedHours}:${minutes} ${ampm}`;
  }

  private getCustomerName(unit: any): string {
    if (unit.stakeholder) {
      return unit.stakeholder.name || unit.stakeholder.full_name || '';
    }
    return '';
  }

  getPropertyUnitDetails(
    id: string,
    mapResponse: boolean = true,
  ): Observable<any> {
    return this.api
      .get<any>(PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_DETAIL(id))
      .pipe(
        map((response) => {
          // Extract data from the wrapped response and map fields
          const unit = response.data || response;
          return mapResponse ? this.mapPropertyUnit(unit) : unit;
        }),
      );
  }

  createPropertyUnit(unit: FormData): Observable<PropertyUnit> {
    return this.api.post<PropertyUnit>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNITS,
      unit,
    );
  }

  updatePropertyUnit(
    id: string,
    unit: FormData,
    buttonId?: string,
  ): Observable<PropertyUnit> {
    let options: any = {};

    if (buttonId) {
      options.context = new HttpContext().set(BUTTON_LOADING_ID, buttonId);
    }

    return this.api.patch<PropertyUnit>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_DETAIL(id),
      unit,
      options,
    );
  }

  deletePropertyUnit(id: string): Observable<any> {
    return this.api.delete(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_DETAIL(id),
    );
  }

  getPropertyRelations(viewName: string): Observable<any> {
    return this.api.get<any>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_RELATIONS(viewName),
    );
  }

  uploadPropertyThumbnail(
    clientId: number,
    unitId: number,
    thumbnail: FormData,
  ): Observable<any> {
    let options: any = {
      observe: 'events' as const,
      reportProgress: true,
    };

    return this.api.post<any>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_THUMBNAIL(clientId, unitId),
      thumbnail,
      options,
    );
  }

  deletePropertyThumbnail(clientId: number, unitId: number): Observable<any> {
    return this.api.delete(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_THUMBNAIL(clientId, unitId),
    );
  }

  getPropertyUnitFileTypes(): Observable<any> {
    return this.api.get<any>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_FILE_TYPES,
    );
  }

  getPropertyUnitFiles(unitId: number): Observable<any> {
    return this.api.get<any>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_FILES(unitId),
    );
  }

  uploadPropertyUnitFile(unitId: number, fileData: FormData): Observable<any> {
    let options: any = {
      observe: 'events' as const,
      reportProgress: true,
    };
    return this.api.post<any>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_FILES(unitId),
      fileData,
      options,
    );
  }

  updatePropertyUnitFile(
    unitId: number,
    fileId: number,
    fileData: FormData,
  ): Observable<any> {
    let options: any = {
      observe: 'events' as const,
      reportProgress: true,
    };
    return this.api.put<any>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_FILE(unitId, fileId),
      fileData,
      options,
    );
  }

  deletePropertyUnitFile(unitId: number, fileId: number): Observable<any> {
    return this.api.delete(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_FILE(unitId, fileId),
    );
  }

  loadCustomerUnits(serviceId: number, customerId: number): Observable<any> {
    return this.api
      .get<any>(
        PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_UNIT_CUSTOMER_UNITS(
          serviceId.toString(),
          customerId.toString(),
        ),
      )
      .pipe(
        map((response) => {
          const transformedData = (response.data || []).map((unit: any) => ({
            ...this.mapPropertyUnit(unit),
            packages: unit.packages || [] 
          }));
          return transformedData;
        }),
      );
  }

  addCustomerUnit(customerId: number, unit: PropertyUnit): Observable<any> {
    return this.api.post<any>(
      PROPERTY_UNITS_ENDPOINTS.V2.PROPERTY_CUSTOMER_UNITS(
        customerId.toString(),
      ),
      unit,
    );
  }
}
